import { Formik, Form, Field, ErrorMessage } from 'formik';
import { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import * as Yup from 'yup';
import { ROUTES } from '../../config/routes';
import { useAppDispatch, useAppSelector } from '../../redux/store';
import { loginThunk } from '../../redux/slices/authSlice';

const LoginSchema = Yup.object({
  username: Yup.string().required('Username or email is required'),
  password: Yup.string().required('Password is required'),
});

const LoginView = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { status, error } = useAppSelector((s) => s.auth);

  useEffect(() => {
    if (status === 'authenticated') {
      navigate(ROUTES.DASHBOARD, { replace: true });
    }
  }, [status, navigate]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-50">
      <div className="w-full max-w-md rounded-2xl bg-white p-8 shadow-md">
        <h1 className="text-2xl font-semibold text-tfs-primary">TFSIQ</h1>
        <p className="mt-1 text-sm text-slate-500">Sign in to continue</p>

        <Formik
          initialValues={{ username: '', password: '' }}
          validationSchema={LoginSchema}
          onSubmit={async (values, { setSubmitting }) => {
            const identifier = values.username.trim();
            const isEmail = identifier.includes('@');
            await dispatch(
              loginThunk({
                ...(isEmail ? { email: identifier } : { username: identifier }),
                password: values.password,
              }),
            );
            setSubmitting(false);
          }}
        >
          {({ isSubmitting }) => (
            <Form className="mt-6 space-y-4">
              <div>
                <label htmlFor="username" className="block text-sm font-medium text-slate-700">
                  Username or email
                </label>
                <Field
                  id="username"
                  name="username"
                  type="text"
                  autoComplete="username"
                  className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-tfs-primary focus:outline-none focus:ring-1 focus:ring-tfs-primary"
                />
                <ErrorMessage name="username" component="p" className="mt-1 text-xs text-red-600" />
              </div>

              <div>
                <label htmlFor="password" className="block text-sm font-medium text-slate-700">
                  Password
                </label>
                <Field
                  id="password"
                  name="password"
                  type="password"
                  autoComplete="current-password"
                  className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-tfs-primary focus:outline-none focus:ring-1 focus:ring-tfs-primary"
                />
                <ErrorMessage name="password" component="p" className="mt-1 text-xs text-red-600" />
              </div>

              {error && (
                <div
                  role="alert"
                  className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700"
                >
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={isSubmitting || status === 'loading'}
                className="w-full rounded-md bg-tfs-primary px-4 py-2 text-sm font-medium text-white transition-opacity hover:opacity-90 disabled:opacity-50"
              >
                {status === 'loading' ? 'Signing in…' : 'Log in'}
              </button>
            </Form>
          )}
        </Formik>

        <div className="mt-6 flex items-center justify-between text-xs text-slate-500">
          <Link to={ROUTES.FORGOT_PASSWORD} className="hover:text-tfs-primary hover:underline">
            Forgot password?
          </Link>
          <Link to={ROUTES.REGISTER} className="hover:text-tfs-primary hover:underline">
            Create an account
          </Link>
        </div>
      </div>
    </div>
  );
};

export default LoginView;
